/**
 * Dependency Discovery for Multi-Doc Export
 *
 * sl-ht07.4: Scans a document's blocks for cross-doc references and returns
 * the set of external doc IDs that this document depends on.
 *
 * Used before snapshot export to offer the user inclusion of dependent docs.
 */

import type { Store } from '@blocksuite/store';

/**
 * Discovers all external document IDs referenced by blocks in the given doc.
 *
 * Scans:
 * - Automation blocks: sourceDocId, inputTables[].docId, outputTables[].docId
 * - Ghost blocks: targetDocId
 * - Database blocks: relation column data.targetDocId
 *
 * Returns only IDs of OTHER docs (filters out the doc's own ID, null, empty).
 */
export function discoverDocDependencies(store: Store): Set<string> {
  const ownDocId = store.id;
  const deps = new Set<string>();

  const root = store.root;
  if (!root) return deps;

  walkBlocks(root, block => {
    switch (block.flavour) {
      case 'affine:automation':
        collectAutomationDeps(block.props, deps);
        break;
      case 'affine:ghost':
        collectGhostDeps(block.props, deps);
        break;
      case 'affine:database':
        collectRelationDeps(block.props, deps);
        break;
    }
  });

  // Remove self-references
  deps.delete(ownDocId);

  return deps;
}

/**
 * Discovers dependencies across multiple docs, returning the union of all
 * referenced doc IDs that are NOT already in the input set.
 */
export function discoverDocDependenciesMulti(stores: Store[]): Set<string> {
  const inputDocIds = new Set(stores.map(s => s.id));
  const allDeps = new Set<string>();

  for (const store of stores) {
    for (const dep of discoverDocDependencies(store)) {
      if (!inputDocIds.has(dep)) {
        allDeps.add(dep);
      }
    }
  }

  return allDeps;
}

// ─── Internal ────────────────────────────────────────────────────────────────

interface BlockLike {
  flavour: string;
  props: Record<string, unknown>;
  children: BlockLike[];
}

function walkBlocks(block: BlockLike, fn: (b: BlockLike) => void): void {
  fn(block);
  for (const child of block.children) {
    walkBlocks(child, fn);
  }
}

function addIfValid(deps: Set<string>, id: unknown): void {
  if (typeof id === 'string' && id.length > 0) {
    deps.add(id);
  }
}

function collectAutomationDeps(
  props: Record<string, unknown>,
  deps: Set<string>
): void {
  addIfValid(deps, props.sourceDocId);

  const inputTables = props.inputTables as
    | Array<{ docId?: string }>
    | undefined;
  if (inputTables) {
    for (const t of inputTables) {
      addIfValid(deps, t.docId);
    }
  }

  const outputTables = props.outputTables as
    | Array<{ docId?: string }>
    | undefined;
  if (outputTables) {
    for (const t of outputTables) {
      addIfValid(deps, t.docId);
    }
  }
}

function collectGhostDeps(
  props: Record<string, unknown>,
  deps: Set<string>
): void {
  addIfValid(deps, props.targetDocId);
}

function collectRelationDeps(
  props: Record<string, unknown>,
  deps: Set<string>
): void {
  const columns = props.columns as
    | Array<{ type?: string; data?: Record<string, unknown> }>
    | undefined;
  if (!columns) return;

  for (const col of columns) {
    if (col.type === 'relation' && col.data) {
      addIfValid(deps, col.data.targetDocId);
    }
  }
}
